import * as React from 'react'
import clsx from 'clsx'
import style from './index.module.scss'
import { ButtonBase, ButtonBaseProps } from '../ButtonBase'
import { capitalize } from '../../utils'
import { SxProps, sx as sxFunc } from '../sx'

export interface ButtonClasses {
  /** Styles applied to the root element. */
  root: string
  /** State class applied to the ButtonBase root element if the button is keyboard focused. */
  focusVisible: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  fullWidth: string
  /** Styles applied to the startIcon element if supplied. */
  startIcon: string
  /** Styles applied to the endIcon element if supplied. */
  endIcon: string
  /** Styles applied to the children if `iconOrientation="horizontal"`. */
  iconOrientationHorizontal: string
  /** Styles applied to the children if `iconOrientation="vertical"`. */
  iconOrientationVertical: string
  /** State class applied to the root element if `selected={true}`. */
  selected: string
}

export interface ButtonProps extends Omit<ButtonBaseProps, 'classes'> {
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<ButtonClasses>
  /**
   * The color of the component.
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  /**
   * If `true`, the  keyboard focus ripple is disabled.
   * @default false
   */
  disableFocusRipple?: boolean
  /**
   * Element placed before the children.
   */
  startIcon?: JSX.Element
  /**
   * Element placed after the children.
   */
  endIcon?: JSX.Element
  /**
   * If `true`, the button will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean
  /**
   * The URL to link to when the button is clicked.
   * If defined, an `a` element will be used as the root node.
   */
  href?: string
  /**
   * The size of the component.
   * `small` is equivalent to the dense button styling.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'

  /**
   * The variant to use.
   * @default 'contained'
   */
  variant?: 'contained' | 'outlined' | 'text'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The icon orientation prop allows to set icon position
   * if iconOrientation is set to horizontal, then we can set start icon at left & end icon at right place.
   * if iconOrientation is set to vertical, then we can set start icon at top & end icon at bottom place.
   * @default 'horizontal'
   */
  iconOrientation?: 'horizontal' | 'vertical'
  /**
   * If `true`, the button is rendered in an active state.
   */
  selected?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const {
      children,
      color = 'primary',
      className,
      classes,
      disabled = false,
      disableFocusRipple = false,
      startIcon: startIconProp,
      endIcon: endIconProp,
      iconOrientation = 'horizontal',
      focusVisibleClassName,
      fullWidth = false,
      size = 'medium',
      type,
      variant = 'contained',
      selected,
      sx,
      ...other
    } = props

    const _classes = clsx(
      style.root,
      classes?.root,
      // @ts-ignore
      style[`${color}--${variant}`],
      disabled && [style.disabled, classes?.disabled],
      selected && [style.active, classes?.selected],
      style[size],
      fullWidth && [style.fullWidth, classes?.fullWidth],
      style[`iconOrientation--${iconOrientation}`],
      // @ts-ignore
      classes?.[`iconOrientation${capitalize(iconOrientation)}`],
      className,
      sxFunc(sx)
    )

    const startIcon = startIconProp && (
      <span className={clsx(style.startIcon, classes?.startIcon, style[size])}>
        {startIconProp}
      </span>
    )

    const endIcon = endIconProp && (
      <span className={clsx(style.endIcon, classes?.endIcon, style[size])}>
        {endIconProp}
      </span>
    )

    return (
      <ButtonBase
        className={_classes}
        disabled={disabled}
        focusRipple={!disableFocusRipple}
        focusVisibleClassName={clsx(
          classes?.focusVisible,
          focusVisibleClassName
        )}
        ref={ref}
        type={type}
        {...other}
      >
        {startIcon}
        {children}
        {endIcon}
      </ButtonBase>
    )
  }
)
